import { MODEL_NAMES } from '../../../constants/index';
import { ROUTE_MAP } from '../../../services/messaging-navigation';
import { conversationHasExactContacts } from '../../../utils/messaging/conversation';
import { filterSystemAndCurrentUser } from '../../../utils/messaging/user';
import { service } from '@ember/service';
import { setOwner } from '@ember/application';
import { task, waitForProperty } from 'ember-concurrency';

export default class WebsocketActivityHandlersConversation {
  @service messaging;
  @service messagingNavigation;
  @service store;

  constructor(owner) {
    setOwner(this, owner);
  }

  get onConversationRoute() {
    return this.messagingNavigation.isCurrentRoute(ROUTE_MAP.conversation);
  }

  // To prevent the addition of duplicates when a user creates a conversation themself, we need to
  // wait for any pending conversation creation to finish (and be assigned an id) before handling
  // the creation activity (which compares ids to prevent duplicates).
  waitForCreateConversationToComplete = task(async () => {
    return await waitForProperty(
      this.messagingNavigation.currentRoute.handler,
      'saveNewConversation.isRunning',
      false
    );
  });

  handleActivity(response) {
    const statusHandlers = {
      updated: this.#handleUpdatedActivity,
      read: this.#handleReadActivity,
      created: this.#handleCreationActivity,
    };
    const handler = response.status ? statusHandlers[response.status] : statusHandlers.updated;
    handler.call(this, response.payload);
  }

  #handleUpdatedActivity(payload) {
    const record = this.#ensureConversationInStore(payload.data.id, payload);

    if (!this.#conversationInList(this.messaging.conversations, record.id)) {
      this.#addOrReplaceConversationRecord(this.messaging.conversations, record);
    }

    const currentConversation = this.messagingNavigation.currentRoute.handler.conversation;
    if (currentConversation?.id === record.id && !record.inactive) {
      this.messagingNavigation.currentRoute.handler.resetInactiveReasons();
    }
  }

  #handleReadActivity(payload) {
    const conversation = this.store.normalize(MODEL_NAMES.CONVERSATION, payload.data).data;
    this.messaging.currentProfile?.removeUnreadConversation(conversation.id);
  }

  async #handleCreationActivity(payload) {
    if (this.onConversationRoute) {
      await this.waitForCreateConversationToComplete.perform();
    }

    const conversationId = payload.data.id;
    const persistedConversation = this.#ensureConversationInStore(conversationId, payload);
    const conversations = this.messaging.conversations;

    if (this.#conversationInList(conversations, conversationId)) return;

    this.#addOrReplaceConversationRecord(conversations, persistedConversation);
  }

  #addOrReplaceConversationRecord(conversations, persistedConversation) {
    const persistedConversationUsers = filterSystemAndCurrentUser(
      persistedConversation.users,
      this.messaging.currentProfile?.user
    );
    const matchingUnsavedConversation = conversations.content.find(
      conv => conv.isNew && conversationHasExactContacts(conv, persistedConversationUsers)
    );

    if (matchingUnsavedConversation) {
      persistedConversation.messageDraft = matchingUnsavedConversation.messageDraft;

      const index = conversations.content.indexOf(matchingUnsavedConversation);
      conversations.replace(index, 1, [persistedConversation]);

      const currentConversation = this.onConversationRoute
        ? this.messagingNavigation.currentRoute.handler.conversation
        : null;
      if (matchingUnsavedConversation === currentConversation) {
        this.messagingNavigation.toConversation(persistedConversation);
      }
    } else {
      conversations.unshiftObjects([persistedConversation]);
    }
  }

  #conversationInList(conversations, conversationId) {
    return conversations.content.some(conv => conv.id === conversationId);
  }

  #ensureConversationInStore(conversationId, payload) {
    this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);
  }
}
